/** Finance web helpers: data hooks, labels, money display and small shared pieces. */
import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { Money, Tone } from "../../../contract";
import { can, type Me } from "../../../contract";
import { formatDate, formatMoney } from "../../../web/lib/format";
import { t } from "../../../web/lib/i18n";
import { queryClient, useApi } from "../../../web/lib/query";
import { useRanchToday } from "../../../web/lib/session";
import { Chip, Icon, Row, Select, TextInput, cx } from "../../../web/ui/kit";
import { DOC_KINDS, PERIODS } from "../contract";
import { periodRange, type PeriodKey } from "../shared/money";
import type { Category, DocKind, DocListItem, DocState, Problem, SetupDTO } from "../shared/types";

export const BASE = "/m/finance";

export function useSetup() {
  return useApi<SetupDTO>(`${BASE}/setup`, { staleTime: 60_000 });
}

/** Finance access, read from the signed-in session cache (safe outside hooks, e.g. section `show`). */
export function canFinanceNow(): boolean {
  const me = queryClient.getQueryData<Me | null>(["/me"]);
  return can(me?.membership?.role, "finance.read");
}

export function categoryName(c: Category | undefined, key?: string | null): string {
  if (!c) return key ? key : t("Uncategorized");
  return c.builtin_name ? t(c.name) : c.name;
}

export function useCategoryNames(): (key: string | null | undefined) => string {
  const setup = useSetup();
  const map = useMemo(() => new Map((setup.data?.categories ?? []).map((c) => [c.key, c])), [setup.data]);
  return (key) => categoryName(key ? map.get(key) : undefined, key);
}

export function useEnterpriseNames(): (id: string | null | undefined) => string {
  const setup = useSetup();
  const map = useMemo(() => new Map((setup.data?.enterprises ?? []).map((e) => [e.id, e.name])), [setup.data]);
  return (id) => (id ? map.get(id) ?? "" : t("Not assigned"));
}

export const money = (minor: number, currency = "USD"): Money => ({ minor, currency });
export const fmt = (minor: number, currency = "USD") => formatMoney(money(minor, currency));

export function kindLabel(kind: string): string {
  return t(DOC_KINDS[kind as DocKind]?.label ?? kind);
}

export function kindIcon(kind: string): string {
  return kind === "income" || kind === "invoice" ? "coins" : kind === "payment" ? "money" : kind === "statement" ? "file" : "receipt";
}

export const STATE_TONE: Record<DocState, Tone> = { draft: "neutral", void: "neutral", settled: "good", open: "warn", partial: "warn", paid: "good" };

/** Plain words for where a document stands. */
export function stateLabel(d: Pick<DocListItem, "state" | "kind" | "overdue" | "direction">): string {
  if (d.state === "void") return t("Void");
  if (d.state === "draft") return t("Draft");
  if (d.overdue) return t("Overdue");
  if (d.state === "settled") return d.direction === "in" ? t("Received") : d.kind === "payment" ? t("Payment") : t("Paid");
  if (d.state === "paid") return d.kind === "invoice" ? t("Paid in full") : t("Paid");
  if (d.state === "partial") return t("Partly paid");
  return d.kind === "invoice" ? t("Not paid yet") : t("To pay");
}

export function StateChip({ doc }: { doc: Pick<DocListItem, "state" | "kind" | "overdue" | "direction"> }) {
  const tone: Tone = doc.overdue ? "bad" : STATE_TONE[doc.state];
  return <Chip tone={tone} icon={doc.overdue ? "warn" : doc.state === "paid" || doc.state === "settled" ? "done" : undefined}>{stateLabel(doc)}</Chip>;
}

export function problemText(p: Problem): string {
  switch (p.code) {
    case "lines_mismatch": return t("Lines add up to {{lines}}, total is {{total}}", { lines: fmt(Number(p.values?.lines ?? 0)), total: fmt(Number(p.values?.total ?? 0)) });
    case "uncategorized": return t("A line has no category");
    case "unknown_category": return t("A line has a category that isn't in your list");
    case "no_lines": return t("No lines saying what it was for");
    case "unapplied_payment": return t("{{amount}} not matched to a bill or invoice", { amount: fmt(Number(p.values?.amount ?? 0)) });
  }
}

/** Amount as people read it: money in shown with a plus. */
export function Amount({ value, direction, strong, className }: { value: Money; direction?: "in" | "out" | null; strong?: boolean; className?: string }) {
  return <span className={cx("finance-amount", direction === "in" && "is-in", strong && "is-strong", className)}>{direction === "in" && value.minor > 0 ? "+" : ""}{formatMoney(value)}</span>;
}

export function DocRow({ doc, showContact = true }: { doc: DocListItem; showContact?: boolean }) {
  const title = (showContact ? doc.contact?.label : null) || doc.memo || doc.number || kindLabel(doc.kind);
  const meta = [formatDate(doc.doc_date), kindLabel(doc.kind), showContact && doc.contact && doc.memo ? doc.memo : null,
    doc.balance.minor > 0 && doc.kind !== "payment" ? t("{{amount}} still owed", { amount: formatMoney(doc.balance) }) : null].filter(Boolean).join(" · ");
  return (
    <Row icon={kindIcon(doc.kind)} tone="money" to={`/ranch/finances/${doc.id}`} title={<span className={cx(doc.state === "void" && "is-void")}>{title}</span>} meta={meta}
      trailing={<span className="finance-row-end"><Amount value={doc.total} direction={doc.direction} />{(doc.state !== "settled" && doc.state !== "paid") || doc.overdue ? <StateChip doc={doc} /> : null}{doc.problems.length > 0 && <Icon name="warn" size={16} className="finance-warn" label={t("Needs a look")} />}{doc.has_attachment && <Icon name="image" size={16} label={t("Has a photo")} />}</span>} />
  );
}

export function PeriodPicker({ value, onChange, custom, onCustom, options = ["this_month", "ytd", "last_12_months", "last_year"] }: {
  value: PeriodKey | "custom"; onChange: (v: PeriodKey | "custom") => void; custom?: { from: string; to: string }; onCustom?: (r: { from: string; to: string }) => void; options?: PeriodKey[];
}) {
  return (
    <div className="finance-period">
      <Select aria-label={t("Period")} value={value} onChange={(e) => onChange(e.target.value as PeriodKey | "custom")}>
        {options.map((k) => <option key={k} value={k}>{t(PERIODS[k])}</option>)}
        {onCustom && <option value="custom">{t("Pick dates")}</option>}
      </Select>
      {value === "custom" && onCustom && custom && (
        <span className="finance-dates">
          <TextInput type="date" aria-label={t("From")} value={custom.from} onChange={(e) => onCustom({ ...custom, from: e.target.value })} />
          <TextInput type="date" aria-label={t("To")} value={custom.to} onChange={(e) => onCustom({ ...custom, to: e.target.value })} />
        </span>
      )}
    </div>
  );
}

/** A period choice plus the query string for it. */
export function usePeriod(initial: PeriodKey = "ytd", key = "finance.period") {
  const setup = useSetup();
  const [period, setPeriod] = useState<PeriodKey | "custom">(() => {
    try { const v = localStorage.getItem(`hondo.v2.${key}`); return v ? JSON.parse(v) : initial; } catch { return initial; }
  });
  const today = useRanchToday();
  const [custom, setCustom] = useState(() => periodRange("ytd", today, setup.data?.settings.fiscal_start_month ?? 1));
  const choose = (v: PeriodKey | "custom") => { setPeriod(v); try { localStorage.setItem(`hondo.v2.${key}`, JSON.stringify(v)); } catch { /* optional */ } };
  const range = period === "custom" ? custom : periodRange(period, today, setup.data?.settings.fiscal_start_month ?? 1);
  const query = period === "custom" ? `from=${custom.from}&to=${custom.to}` : `period=${period}`;
  return { period, setPeriod: choose, custom, setCustom, range, query };
}

export function NoAccess() {
  return (
    <div className="empty">
      <span className="empty-icon"><Icon name="shield" size={26} /></span>
      <p className="empty-title">{t("Finances are private")}</p>
      <p className="empty-body">{t("Only owners, managers and the bookkeeper can see money. Ask the owner if you need access.")}</p>
      <Link to="/ranch" className="btn btn-secondary btn-md">{t("Back to Ranch")}</Link>
    </div>
  );
}
